/**
 * Shared caches for workflow node configuration panels.
 * Prevents repeated API calls on every node selection click.
 *
 * Covers:
 *  - /datasources              → used by DuplicateDetectorConfig, DatabaseLookupConfig
 *  - /users/by-role?role=...   → used by ApprovalConfig (legacy)
 *  - /users/                   → used by ApprovalConfig user picker
 */
import { apiFetch } from '@/api';

const API_BASE_URL = (() => { const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, ''); return base.endsWith('/api/v1') ? base : base + '/api/v1'; })();

const getToken = () => {
    try {
        const s = sessionStorage.getItem('access_token');
        if (s) return s;
        const t = localStorage.getItem('token') || localStorage.getItem('authToken');
        if (t) return t;
        const st = localStorage.getItem('auth-storage');
        if (st) return JSON.parse(st).state?.token || null;
    } catch { }
    return null;
};

// ─── Datasources cache ────────────────────────────────────────────────────────
let _dsCache: any[] | null = null;
let _dsInflight: Promise<any[]> | null = null;

export async function fetchDatasources(): Promise<any[]> {
    if (_dsCache !== null) return _dsCache;
    if (_dsInflight) return _dsInflight;
    _dsInflight = apiFetch('/datasources', null, { method: 'GET' })
        .then((d: any) => {
            _dsCache = Array.isArray(d) ? d : [];
            _dsInflight = null;
            return _dsCache!;
        })
        .catch(() => {
            _dsInflight = null;
            return [] as any[];
        });
    return _dsInflight;
}

export function invalidateDatasourceCache(): void {
    _dsCache = null;
    _dsInflight = null;
}

// ─── Users-by-role cache ──────────────────────────────────────────────────────
const _roleCache = new Map<string, any[]>();
const _roleInflight = new Map<string, Promise<any[]>>();

export async function fetchUsersByRole(role: string): Promise<any[]> {
    if (_roleCache.has(role)) return _roleCache.get(role)!;
    if (_roleInflight.has(role)) return _roleInflight.get(role)!;

    const token = getToken();
    const p = fetch(`${API_BASE_URL}/users/by-role?role=${encodeURIComponent(role)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    })
        .then(res => res.json())
        .then(users => {
            const list = Array.isArray(users) ? users : [];
            _roleCache.set(role, list);
            _roleInflight.delete(role);
            return list;
        })
        .catch(() => {
            _roleInflight.delete(role);
            return [] as any[];
        });

    _roleInflight.set(role, p);
    return p;
}

/** Call after user management changes so next selection re-fetches. */
export function invalidateRoleCache(role?: string): void {
    if (role) {
        _roleCache.delete(role);
        _roleInflight.delete(role);
    } else {
        _roleCache.clear();
        _roleInflight.clear();
    }
}

// ─── All-users cache (keyed by tenantId so each tenant context is isolated) ───
const _allUsersCache = new Map<string, any[]>();
const _allUsersInflight = new Map<string, Promise<any[]>>();

/**
 * Fetch users visible to the current user, optionally scoped to a tenantId.
 * The cache is keyed by tenantId so a Platform Admin's cross-tenant result
 * does not leak into a Tenant Admin's scoped view.
 */
export async function fetchAllUsers(tenantId?: string): Promise<any[]> {
    const cacheKey = tenantId || '__all__';
    if (_allUsersCache.has(cacheKey)) return _allUsersCache.get(cacheKey)!;
    if (_allUsersInflight.has(cacheKey)) return _allUsersInflight.get(cacheKey)!;

    const token = getToken();
    const url = tenantId
        ? `${API_BASE_URL}/users/?tenant_id=${encodeURIComponent(tenantId)}`
        : `${API_BASE_URL}/users/`;

    const p = fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
    })
        .then(res => res.json())
        .then(users => {
            const list = Array.isArray(users) ? users : [];
            _allUsersCache.set(cacheKey, list);
            _allUsersInflight.delete(cacheKey);
            return list;
        })
        .catch(() => {
            _allUsersInflight.delete(cacheKey);
            return [] as any[];
        });

    _allUsersInflight.set(cacheKey, p);
    return p;
}

export function invalidateAllUsersCache(tenantId?: string): void {
    if (tenantId) {
        _allUsersCache.delete(tenantId);
        _allUsersInflight.delete(tenantId);
    } else {
        _allUsersCache.clear();
        _allUsersInflight.clear();
    }
}
